import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import Label from "../Label/Label";
import CustomButton from "../CustomButton/CustomButton";
import ModalPopup from "../ModalPopup/ModalPopup";
import {
  createCareerPosition,
  deleteCareerPosition,
  fetchCareerApplications,
  fetchCareerPositions,
  updateCareerPosition,
} from "../../services/careerApi";
import { formatDisplayDate, getApiErrorMessage } from "../../services/apiClient";
import "./CareerAdminTab.scss";

const getResumeFileName = (application) => {
  const safeName = (application.name || "applicant").replace(/\s+/g, "-");
  const resumeUrl = application.resumeUrl || "";

  if (resumeUrl.startsWith("data:application/pdf")) {
    return `${safeName}-resume.pdf`;
  }

  if (
    resumeUrl.startsWith(
      "data:application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    )
  ) {
    return `${safeName}-resume.docx`;
  }

  if (resumeUrl.startsWith("data:application/msword")) {
    return `${safeName}-resume.doc`;
  }

  try {
    const urlPath = new URL(resumeUrl).pathname;
    const extension = urlPath.split(".").pop();

    if (extension && extension.length <= 5) {
      return `${safeName}-resume.${extension}`;
    }
  } catch {
    // Not a valid http URL — fall through to default name.
  }

  return `${safeName}-resume`;
};

const emptyPositionForm = {
  positionName: "",
  experienceRequired: "",
  jobDescription: "",
  description: "",
  keyResponsibilities: "",
  skillsRequirement: "",
  btnTitle: "Apply Now",
  responsibilities: [{ id: "1", point: "" }],
  requirements: [{ id: "1", point: "" }],
};

const CareerAdminTab = () => {
  const [positions, setPositions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [positionForm, setPositionForm] = useState(emptyPositionForm);
  const [selectedPositionId, setSelectedPositionId] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const loadPositions = async () => {
    try {
      const items = await fetchCareerPositions();
      setPositions(items);
    } catch (error) {
      console.error("Failed to load career positions", error);
    }
  };

  const loadApplications = async (positionId = "") => {
    try {
      const items = await fetchCareerApplications(positionId || undefined);
      setApplications(items);
    } catch (error) {
      console.error("Failed to load applications", error);
    }
  };

  useEffect(() => {
    loadPositions();
    loadApplications();
  }, []);

  const openCreateModal = () => {
    setEditingPosition(null);
    setPositionForm(emptyPositionForm);
    setShowModal(true);
  };

  const openEditModal = (position) => {
    setEditingPosition(position);
    setPositionForm({
      positionName: position.positionName || "",
      experienceRequired: position.experienceRequired || "",
      jobDescription: position.jobDescription || "",
      description: position.description || "",
      keyResponsibilities: position.keyResponsibilities || "",
      skillsRequirement: position.skillsRequirement || "",
      btnTitle: position.btnTitle || "Apply Now",
      responsibilities: position.responsibilities?.length
        ? position.responsibilities
        : [{ id: "1", point: "" }],
      requirements: position.requirements?.length
        ? position.requirements
        : [{ id: "1", point: "" }],
    });
    setShowModal(true);
  };

  const handleSavePosition = async () => {
    if (!positionForm.positionName.trim()) {
      window.alert("Position name is required.");
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        ...positionForm,
        responsibilities: positionForm.responsibilities.filter((item) =>
          item.point?.trim(),
        ),
        requirements: positionForm.requirements.filter((item) =>
          item.point?.trim(),
        ),
      };

      if (editingPosition?.positionId) {
        await updateCareerPosition(editingPosition.positionId, payload);
      } else {
        await createCareerPosition(payload);
      }

      setShowModal(false);
      await loadPositions();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to save position."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePosition = async (positionId) => {
    if (!window.confirm("Delete this position?")) {
      return;
    }

    try {
      await deleteCareerPosition(positionId);
      await loadPositions();
      await loadApplications(selectedPositionId);
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to delete position."));
    }
  };

  const updateListField = (field, index, value) => {
    setPositionForm((current) => ({
      ...current,
      [field]: current[field].map((item, itemIndex) =>
        itemIndex === index ? { ...item, point: value } : item,
      ),
    }));
  };

  const addListField = (field) => {
    setPositionForm((current) => ({
      ...current,
      [field]: [
        ...current[field],
        { id: String(current[field].length + 1), point: "" },
      ],
    }));
  };

  return (
    <div className="career_Admin_Tab">
      <div className="career_Admin_Header">
        <Label title="Career Management" className="heading" />
        <CustomButton title="Add Job" onClick={openCreateModal} />
      </div>

      <div className="contact_Table career_Positions_Table">
        <ul className="header">
          <li>Position</li>
          <li>Experience</li>
          <li>Created</li>
          <li>Actions</li>
        </ul>
        {positions.map((position) => (
          <ul className="data_List" key={position.positionId}>
            <li>{position.positionName}</li>
            <li>{position.experienceRequired}</li>
            <li>{formatDisplayDate(position.createdAt)}</li>
            <li className="actions">
              <span
                className="icon-pencil paddRight cursorP"
                onClick={() => openEditModal(position)}
              />
              <span
                className="icon-delete cursorP"
                onClick={() => handleDeletePosition(position.positionId)}
              />
            </li>
          </ul>
        ))}
      </div>

      <div className="career_Applications_Section">
        <div className="career_Admin_Header">
          <Label title="Job Applications" className="heading" />
          <Form.Select
            value={selectedPositionId}
            onChange={(event) => {
              const value = event.target.value;
              setSelectedPositionId(value);
              loadApplications(value);
            }}
          >
            <option value="">All Positions</option>
            {positions.map((position) => (
              <option key={position.positionId} value={position.positionId}>
                {position.positionName}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="contact_Table career_Applications_Table">
          <ul className="header">
            <li>Name</li>
            <li>Email</li>
            <li>Phone</li>
            <li>Position</li>
            <li>Applied On</li>
            <li>Resume</li>
          </ul>
          {applications.map((application) => (
            <ul className="data_List" key={application.applicationId}>
              <li>{application.name}</li>
              <li>{application.email}</li>
              <li>{application.phoneNumber}</li>
              <li>{application.positionName}</li>
              <li>{formatDisplayDate(application.createdAt)}</li>
              <li className="resume_Link">
                {application.resumeUrl ? (
                  <a
                    href={application.resumeUrl}
                    download={getResumeFileName(application)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="download_Resume"
                  >
                    Download Resume
                  </a>
                ) : (
                  <span className="no_Resume">No resume</span>
                )}
              </li>
            </ul>
          ))}
        </div>
      </div>

      <ModalPopup
        showModal={showModal}
        onHide={() => setShowModal(false)}
        onHideClick={() => setShowModal(false)}
        onButtonClick={handleSavePosition}
        headerTitle={editingPosition ? "Edit Job Position" : "Add Job Position"}
        buttonTitle={isSaving ? "Saving..." : "Save Position"}
      >
        <div className="career_Position_Form">
          <Form.Control
            className="mb-3"
            placeholder="Position Name"
            value={positionForm.positionName}
            onChange={(event) =>
              setPositionForm((current) => ({
                ...current,
                positionName: event.target.value,
              }))
            }
          />
          <Form.Control
            className="mb-3"
            placeholder="Experience Required"
            value={positionForm.experienceRequired}
            onChange={(event) =>
              setPositionForm((current) => ({
                ...current,
                experienceRequired: event.target.value,
              }))
            }
          />
          <Form.Control
            className="mb-3"
            placeholder="Job Description Heading"
            value={positionForm.jobDescription}
            onChange={(event) =>
              setPositionForm((current) => ({
                ...current,
                jobDescription: event.target.value,
              }))
            }
          />
          <Form.Control
            as="textarea"
            className="mb-3"
            rows={3}
            placeholder="Description"
            value={positionForm.description}
            onChange={(event) =>
              setPositionForm((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
          />
          <Form.Control
            className="mb-3"
            placeholder="Key Responsibilities Heading"
            value={positionForm.keyResponsibilities}
            onChange={(event) =>
              setPositionForm((current) => ({
                ...current,
                keyResponsibilities: event.target.value,
              }))
            }
          />
          {positionForm.responsibilities.map((item, index) => (
            <Form.Control
              key={`res-${index}`}
              className="mb-2"
              placeholder={`Responsibility ${index + 1}`}
              value={item.point}
              onChange={(event) =>
                updateListField("responsibilities", index, event.target.value)
              }
            />
          ))}
          <CustomButton
            title="Add Responsibility"
            onClick={() => addListField("responsibilities")}
          />
          <Form.Control
            className="mb-3 mt-3"
            placeholder="Skills Requirement Heading"
            value={positionForm.skillsRequirement}
            onChange={(event) =>
              setPositionForm((current) => ({
                ...current,
                skillsRequirement: event.target.value,
              }))
            }
          />
          {positionForm.requirements.map((item, index) => (
            <Form.Control
              key={`req-${index}`}
              className="mb-2"
              placeholder={`Requirement ${index + 1}`}
              value={item.point}
              onChange={(event) =>
                updateListField("requirements", index, event.target.value)
              }
            />
          ))}
          <CustomButton
            title="Add Requirement"
            onClick={() => addListField("requirements")}
          />
        </div>
      </ModalPopup>
    </div>
  );
};

export default CareerAdminTab;
